import { components } from "@/api-schema";
import { formatApiDateOnlyToLocale, formatDateToLocale } from "@/common/helpers/format-date";
import { getVehicleModelName } from "@/common/helpers/get-vehicle-model-name";
import { VEHICLE_COLOR_LABELS, VEHICLE_KIND_LABELS } from "@/common/helpers/vehicle-type-labels";
import { DeviceType } from "@/common/hooks/use-device";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { ImplementedAccessorColumnDef, ImplementedDisplayColumnDef } from "@/components/data-table/types";
import { EmptyValue } from "@/components/ui/empty-value";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { vehicleHasPendingRelocationOrder } from "@/common/helpers/vehicle-has-pending-relocation-order";
import { VEHICLE_BLOCK_REASONS } from "@/components/vehicle-block/_helpers/vehicle-block-reasons";
import i18next from "i18next";
import { LockIcon, TruckIcon } from "lucide-react";
import { BranchWithAddress } from "./_components/branch-with-address";

type VehicleTableType = components["schemas"]["Vehicle"] & {
  registrationDate?: string | null;
};
type Brand = components["schemas"]["Brand"];
type Branch = components["schemas"]["Branch"];

function getVehicleColorLabel(color?: string | null): string | undefined {
  return color ? (VEHICLE_COLOR_LABELS[color]?.[i18next.language === "de" ? "de" : "en"] ?? color) : undefined;
}

function getVehicleKindLabel(vehicleKind?: string | null): string | undefined {
  return vehicleKind
    ? (VEHICLE_KIND_LABELS[vehicleKind]?.[i18next.language === "de" ? "de" : "en"] ?? vehicleKind)
    : undefined;
}

function formatRegistrationDate(registrationDate?: string | null): string | null {
  return formatApiDateOnlyToLocale(registrationDate) ?? null;
}

function getVehicleBooleanLabel(value?: boolean | null): string | null {
  if (value == null) {
    return null;
  }

  return value ? i18next.t("common:Yes") : i18next.t("common:No");
}

const widescreenColumns = {
  columnBrand: {
    id: "brand",
    exportPaths: ["modelGeneration.modelFamily.brand.name"],
    size: 100,
    align: "center",
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("common:Brand"),
    accessorFn: (row) => row.modelGeneration?.modelFamily?.brand,
    cell: (row) => {
      const brand = row.getValue();
      if (!brand) return <EmptyValue />;
      return (
        <div className="flex flex-col items-center gap-1">
          <BrandLogo brand={brand} />
          <span className="w-full truncate text-center text-xs">{brand.name}</span>
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<VehicleTableType, Brand | null>,
  columnModelDescription: {
    id: "modelDescription",
    exportPaths: ["modelDescription", "modelGeneration.name", "modelGeneration.modelFamily.name"],
    size: 100,
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("masterdata:ModelDescription"),
    accessorFn: (row) => getVehicleModelName(row),
    cell: (row) => {
      const modelName = row.getValue();
      if (!modelName) return <EmptyValue />;

      const vehicleBlock = row.row.original.vehicleBlock;
      const isBlocked = vehicleBlock !== null && vehicleBlock !== undefined;

      if (!isBlocked) {
        return modelName;
      }

      const reason = vehicleBlock.reason;
      const reasonLabel = VEHICLE_BLOCK_REASONS[reason];

      const displayLabel = reasonLabel ? i18next.t(reasonLabel) : reason;
      const blockedUntil = vehicleBlock.blockedUntil;
      const formattedDate = blockedUntil ? formatDateToLocale(blockedUntil, { dateOnly: true }) : null;

      return (
        <div className="flex items-center gap-2">
          <span>{modelName}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <LockIcon className="text-muted-foreground h-4 w-4" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>
                  {i18next.t("fit:Blocked")}: {displayLabel}
                  {formattedDate && ` bis ${formattedDate}`}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<VehicleTableType, string | null>,
  columnVin: {
    id: "vin",
    size: 100,
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("fit:VIN"),
    accessorFn: (row) => row.vin,
    cell: ({ row }) => row.original.vin,
  } as ImplementedAccessorColumnDef<VehicleTableType, string>,
  columnColor: {
    id: "color",
    size: 100,
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("common:Color"),
    accessorFn: (row) => row.color,
    cell: (row) => {
      const colorLabel = getVehicleColorLabel(row.getValue());
      return automaticCellRenderer(colorLabel);
    },
  } as ImplementedAccessorColumnDef<VehicleTableType, string>,
  columnRegistrationDate: {
    id: "registrationDate",
    size: 120,
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("common:RegistrationDate"),
    accessorFn: (row) => row.registrationDate,
    cell: (row) => {
      const registrationDate = row.getValue();
      if (!registrationDate) {
        return <EmptyValue />;
      }

      return formatRegistrationDate(registrationDate);
    },
  } as ImplementedAccessorColumnDef<VehicleTableType, string | null | undefined>,
  columnReimport: {
    id: "reimport",
    size: 100,
    enableSorting: true,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("masterdata:Reimport"),
    accessorFn: (row) => row.reimport,
    cell: (row) => automaticCellRenderer(getVehicleBooleanLabel(row.getValue())),
  } as ImplementedAccessorColumnDef<VehicleTableType, boolean | null | undefined>,
  columnTransportStatus: {
    id: "transportStatus",
    exportPaths: [],
    size: 150,
    align: "center",
    enableSorting: false,
    visibility: [DeviceType.Widescreen],
    header: () => i18next.t("common:Transport"),
    cell: (row) => {
      if (!vehicleHasPendingRelocationOrder(row.row.original)) {
        return null;
      }

      return (
        <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
          <TruckIcon className="text-primary h-5 w-5" />
        </div>
      );
    },
  } as ImplementedDisplayColumnDef<VehicleTableType>,
};

const widescreenDesktopAndTabletColumns = {
  columnOwningBranch: {
    id: "owningBranch",
    exportPaths: ["owningBranch.name"],
    size: 100,
    enableSorting: true,
    visibility: [DeviceType.Widescreen, DeviceType.Desktop, DeviceType.Tablet],
    header: () => i18next.t("common:OwningBranch"),
    accessorFn: (row) => row.owningBranch,
    cell: (row) => {
      const owningBranch = row.getValue();
      if (!owningBranch) {
        return <EmptyValue />;
      }

      return <BranchWithAddress branch={owningBranch} address={owningBranch.address} className="whitespace-nowrap" />;
    },
  } as ImplementedAccessorColumnDef<VehicleTableType, Branch | null>,
  columnCurrentBranch: {
    id: "currentBranch",
    exportPaths: ["currentBranch.name"],
    size: 100,
    enableSorting: true,
    visibility: [DeviceType.Widescreen, DeviceType.Desktop, DeviceType.Tablet],
    header: () => i18next.t("fit:CurrentBranch"),
    accessorFn: (row) => row.currentBranch,
    cell: (row) => {
      const currentBranch = row.getValue();
      if (!currentBranch) {
        return <EmptyValue />;
      }

      return <BranchWithAddress branch={currentBranch} address={currentBranch.address} className="whitespace-nowrap" />;
    },
  } as ImplementedAccessorColumnDef<VehicleTableType, Branch | null>,
};

const desktopAndTabletColumns = {
  columnBrandModelDescription: {
    id: "brandModelDescription",
    exportPaths: ["modelGeneration.modelFamily.brand.name", "modelDescription"],
    size: 100,
    enableSorting: true,
    align: "center",
    visibility: [DeviceType.Tablet, DeviceType.Desktop],
    header: () => i18next.t("common:Vehicle"),
    accessorFn: (row) => {
      const brandName = row.modelGeneration?.modelFamily?.brand?.name;
      const modelName = getVehicleModelName(row);
      return [brandName, modelName].filter(Boolean).join(" ");
    },
    cell: ({ row }) => {
      const brand = row.original.modelGeneration?.modelFamily?.brand;
      const modelName = getVehicleModelName(row.original);

      if (!brand && !modelName) return <EmptyValue />;

      return (
        <div className="flex flex-col items-center gap-1">
          {brand && (
            <>
              <BrandLogo brand={brand} />
              <span className="w-full truncate text-center text-xs">{brand.name}</span>
            </>
          )}
          {modelName && <span className="w-30 truncate text-center text-xs">{modelName}</span>}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<VehicleTableType, string>,
  columnVinColorTransportStatus: {
    id: "vinColorTransportStatus",
    exportPaths: ["vin", "color", "vehicleKind", "registrationDate", "reimport"],
    size: 200,
    enableSorting: false,
    visibility: [DeviceType.Tablet, DeviceType.Desktop],
    header: () => i18next.t("common:Details"),
    cell: ({ row }) => {
      const vehicle = row.original;
      const vin = vehicle.vin;
      const colorLabel = getVehicleColorLabel(vehicle.color);
      const vehicleKindLabel = getVehicleKindLabel(vehicle.vehicleKind);
      const registrationDate = vehicle.registrationDate;
      const reimportLabel = getVehicleBooleanLabel(vehicle.reimport);

      return (
        <div className="flex flex-col gap-2">
          <div>
            <strong>{i18next.t("fit:VIN")}: </strong>
            {vin}
          </div>
          <div>
            <strong>{i18next.t("common:Color")}: </strong>
            {colorLabel || <EmptyValue />}
          </div>
          <div>
            <strong>{i18next.t("masterdata:VehicleKind")}: </strong>
            {vehicleKindLabel || <EmptyValue />}
          </div>
          <div>
            <strong>{i18next.t("common:RegistrationDate")}: </strong>
            {registrationDate ? formatRegistrationDate(registrationDate) : <EmptyValue />}
          </div>
          <div>
            <strong>{i18next.t("masterdata:Reimport")}: </strong>
            {reimportLabel || <EmptyValue />}
          </div>
          {vehicleHasPendingRelocationOrder(row.original) && (
            <div className="bg-primary/10 flex size-8 items-center justify-center rounded-full">
              <TruckIcon className="text-primary size-4" />
            </div>
          )}
        </div>
      );
    },
  } as ImplementedDisplayColumnDef<VehicleTableType>,
  columnVinColor: {
    id: "vinColor",
    exportPaths: ["vin", "color", "vehicleKind", "registrationDate", "reimport"],
    size: 200,
    enableSorting: false,
    visibility: [DeviceType.Tablet, DeviceType.Desktop],
    header: () => i18next.t("common:Details"),
    cell: ({ row }) => {
      const vehicle = row.original;
      const vin = vehicle.vin;
      const colorLabel = getVehicleColorLabel(vehicle.color);
      const vehicleKindLabel = getVehicleKindLabel(vehicle.vehicleKind);
      const registrationDate = vehicle.registrationDate;
      const reimportLabel = getVehicleBooleanLabel(vehicle.reimport);

      return (
        <div className="flex flex-col gap-2">
          <div>
            <strong>{i18next.t("fit:VIN")}: </strong>
            {vin}
          </div>
          <div>
            <strong>{i18next.t("common:Color")}: </strong>
            {colorLabel || <EmptyValue />}
          </div>
          <div>
            <strong>{i18next.t("masterdata:VehicleKind")}: </strong>
            {vehicleKindLabel || <EmptyValue />}
          </div>
          <div>
            <strong>{i18next.t("common:RegistrationDate")}: </strong>
            {registrationDate ? formatRegistrationDate(registrationDate) : <EmptyValue />}
          </div>
          <div>
            <strong>{i18next.t("masterdata:Reimport")}: </strong>
            {reimportLabel || <EmptyValue />}
          </div>
        </div>
      );
    },
  } as ImplementedDisplayColumnDef<VehicleTableType>,
};

const mobileColumns = {
  columnMobileComplete: {
    id: "mobileComplete",
    exportPaths: [],
    size: 1000,
    enableSorting: false,
    header: () => i18next.t("fit:VehicleAndRoute"),
    visibility: [DeviceType.Mobile],
    cell: ({ row }) => {
      const brand = row.original.modelGeneration?.modelFamily?.brand;
      const brandName = brand?.name;
      const modelName = getVehicleModelName(row.original);
      const vin = row.original.vin;
      const colorLabel = getVehicleColorLabel(row.original.color);
      const vehicleKindLabel = getVehicleKindLabel(row.original.vehicleKind);
      const registrationDate = row.original.registrationDate;
      const reimportLabel = getVehicleBooleanLabel(row.original.reimport);
      const owningBranch = row.original.owningBranch;
      const currentBranch = row.original.currentBranch;

      return (
        <div className="flex flex-col gap-2 py-2 text-left">
          <div className="flex min-w-0 items-center gap-2 text-base font-semibold">
            {brand && (
              <div className="flex items-center justify-center">
                <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />
              </div>
            )}
            {(brandName || modelName) && (
              <span className="truncate">{[brandName, modelName].filter(Boolean).join(" ")}</span>
            )}
          </div>
          <div className="text-muted-foreground flex flex-col gap-1 text-sm">
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:VIN")}:</span>
              <span className="truncate font-mono text-xs">{vin}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:Color")}:</span>
              <span className="truncate">{colorLabel ?? <EmptyValue />}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("masterdata:VehicleKind")}:</span>
              <span className="truncate">{vehicleKindLabel ?? <EmptyValue />}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("common:RegistrationDate")}:</span>
              <span className="truncate">{formatRegistrationDate(registrationDate) ?? <EmptyValue />}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("masterdata:Reimport")}:</span>
              <span className="truncate">{reimportLabel ?? <EmptyValue />}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("common:OwningBranch")}:</span>
              <span className="text-foreground truncate font-medium">
                {owningBranch ? owningBranch.name : i18next.t("common:Unknown")}
              </span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:CurrentBranch")}:</span>
              <span className="text-foreground truncate font-medium">
                {currentBranch ? currentBranch.name : i18next.t("common:Unknown")}
              </span>
            </div>
            {vehicleHasPendingRelocationOrder(row.original) && (
              <div className="bg-primary/10 flex size-8 items-center justify-center rounded-full">
                <TruckIcon className="text-primary size-4" />
              </div>
            )}
          </div>
        </div>
      );
    },
  } as ImplementedDisplayColumnDef<VehicleTableType>,
  columnMobileCompleteWithoutTransportStatus: {
    id: "mobileCompleteWithoutTransportStatus",
    exportPaths: [],
    size: 1000,
    enableSorting: false,
    header: () => i18next.t("fit:VehicleAndRoute"),
    visibility: [DeviceType.Mobile],
    cell: ({ row }) => {
      const brand = row.original.modelGeneration?.modelFamily?.brand;
      const brandName = brand?.name;
      const modelName = getVehicleModelName(row.original);
      const vin = row.original.vin;
      const colorLabel = getVehicleColorLabel(row.original.color);
      const vehicleKindLabel = getVehicleKindLabel(row.original.vehicleKind);
      const registrationDate = row.original.registrationDate;
      const reimportLabel = getVehicleBooleanLabel(row.original.reimport);
      const owningBranch = row.original.owningBranch;
      const currentBranch = row.original.currentBranch;

      return (
        <div className="flex flex-col gap-2 py-2 text-left">
          <div className="flex min-w-0 items-center gap-2 text-base font-semibold">
            {brand && (
              <div className="flex items-center justify-center">
                <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />
              </div>
            )}
            {(brandName || modelName) && (
              <span className="truncate">{[brandName, modelName].filter(Boolean).join(" ")}</span>
            )}
          </div>
          <div className="text-muted-foreground flex flex-col gap-1 text-sm">
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:VIN")}:</span>
              <span className="truncate font-mono text-xs">{vin}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:Color")}:</span>
              <span className="truncate">{colorLabel ?? <EmptyValue />}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("masterdata:VehicleKind")}:</span>
              <span className="truncate">{vehicleKindLabel ?? <EmptyValue />}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("common:RegistrationDate")}:</span>
              <span className="truncate">{formatRegistrationDate(registrationDate) ?? <EmptyValue />}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("masterdata:Reimport")}:</span>
              <span className="truncate">{reimportLabel ?? <EmptyValue />}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("common:OwningBranch")}:</span>
              <span className="text-foreground truncate font-medium">
                {owningBranch ? owningBranch.name : i18next.t("common:Unknown")}
              </span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 font-medium">{i18next.t("fit:CurrentBranch")}:</span>
              <span className="text-foreground truncate font-medium">
                {currentBranch ? currentBranch.name : i18next.t("common:Unknown")}
              </span>
            </div>
          </div>
        </div>
      );
    },
  } as ImplementedDisplayColumnDef<VehicleTableType>,
} as const;

export const vehicleTableDefaultColumns = {
  widescreen: widescreenColumns,
  widescreenDesktopAndTablet: widescreenDesktopAndTabletColumns,
  desktopAndTablet: desktopAndTabletColumns,
  mobile: mobileColumns,
};
